import { useEffect, useRef, useState } from "react"
import { AlertBox, Button, ButtonGroup, FormContainer, FormDescription, FormGroup, FormTitle, Input, Label, LanguageToggle, StepItem, StepLabel, StepNumber, StepperContainer, StepperWrapper, Textarea } from "./styles"
import { useTranslation } from "react-i18next"
import { getData, postData, putData } from "../../../backend/api"
import LanguageTooltip from "../tooltip"
import CustomSelect from "../select"
import { UploadIcon } from "../../../icons"
import { FileContainer } from "../input/styles"
import MultiSelect from "../multiSelect"
import { Logo } from "../../private/common/layout/header/styels"
import { logo } from "../../../images"
import { arrayBufferToBase64, base64ToUint8Array, encryptData, generateAccessPass, importPublicKey } from "../../functions/cryptoUtils"
import { DateTimePicker } from "../datepicker"

const STEPS = [
    { number: 1, label: "department" },
    { number: 2, label: "Category" },
    { number: 3, label: "Description" },
    { number: 4, label: "Check" },
    { number: 5, label: "Password" },
]

export default function StepperForm({ formFields, setLoaderBox, setMessage, api, channel, setIsImageLoaded }) {
  const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(1)
    const [formData, setFormData] = useState({})

    // Initialize formData with default values from formFields
    useEffect(() => {
      if (formFields && formFields.length > 0) {
        const defaultValues = {};
        formFields.forEach(field => {
          if (field.default !== undefined && field.default !== null && field.default !== '') {
            // Validate default value based on field type
            let isValid = true;
            
            // For select/radio fields, check if default is a valid option
            if ((field.type === 'select' || field.type === 'radio') && field.options) {
              const validOptions = field.options.map(opt => opt.name || opt.value || opt.id);
              isValid = validOptions.includes(field.default);
            }
            
            // For required fields, ensure default is not empty
            if (field.required && !field.default) {
              isValid = false;
            }
            
            if (isValid) {
              defaultValues[field.name] = field.default;
            }
          }
        });
        
        // Only update if there are default values and formData is empty
        if (Object.keys(defaultValues).length > 0) {
          setFormData(prev => {
            // Don't overwrite existing values
            const merged = { ...defaultValues };
            Object.keys(prev).forEach(key => {
              if (prev[key] !== undefined && prev[key] !== null && prev[key] !== '') {
                merged[key] = prev[key];
              }
            });
            return merged;
          });
        }
      }
    }, [formFields]);

    const handleNext = (data,nextStep=true) => {
      if(data)
        setFormData((prev) => ({ ...prev, ...data }))
      if(nextStep)
        setCurrentStep((prev) => Math.min(prev + 1, STEPS.length))
    }

    const handleBack = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 1))
    }

    // Helper function to get custom fields for a specific step
    const getCustomFieldsForStep = (stepNumber) => {
        return formFields
            .filter(field => field.isCustom && field.stepNumber === stepNumber)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
    };

    const getStepContent = (step) => {
        switch (step) {
            case 1:
                formFields.filter((field) => field.name === "department").forEach((field) => field.disabled = false);
                const step1CustomFields = getCustomFieldsForStep(1);
                step1CustomFields.forEach((field) => field.disabled = false);
                return {
                    title: t("relevantLocationTitle"),
                    description: t("relevantLocationDescription", {company: channel?.company?.organization}),
                    fields: [
                        ...formFields.filter((field) => field.name === "department"),
                        ...step1CustomFields
                    ],
                }
            case 2:
                formFields.filter((field) => field.name === "category").forEach((field) => field.disabled = false);
                const step2CustomFields = getCustomFieldsForStep(2);
                step2CustomFields.forEach((field) => field.disabled = false);
                return {
                    title: t("incidentCategoryTitle"),
                    description: t("incidentCategoryDescription"),
                    fields: [
                        ...formFields.filter((field) => field.name === "category"),
                        ...step2CustomFields
                    ],
                }
            case 3:
                formFields.filter((field) => ["title","anonymity", "description", "file"].includes(field.name)).forEach((field) => field.disabled = false);
                const step3CustomFields = getCustomFieldsForStep(3);
                step3CustomFields.forEach((field) => field.disabled = false);
                return {
                    title: t("incidentInfoTitle"),
                    description: t("incidentInfoDescription"),
                    fields: [
                        ...formFields.filter((field) => ["title","anonymity", "description", "file"].includes(field.name)),
                        ...step3CustomFields
                    ],
                }
            case 4:
                // Include core fields and all custom fields for preview
                const allCoreFields = formFields.filter((field) => field.name !== "pass" && !field.isCustom);
                const allCustomFields = formFields.filter((field) => field.isCustom);
                [...allCoreFields, ...allCustomFields].forEach((field) => field.disabled = true);
                return {
                    title: t("reviewTitle"),
                    description: t("reviewDescription"),
                    fields: [...allCoreFields, ...allCustomFields],
                }
            case 5:
                return {
                    title: t("savePassTitle"),
                    description: t("savePassDescription"),
                    fields: formFields.filter((field) => field.name === "pass"),
                    bottomTitle: t("savePassBottomTitle"),
                    bottomDescription: t("savePassBottomDescription"),
                }
            default:
                return { title: "", description: "", fields: [] }
        }
    }

    const stepContent = getStepContent(currentStep)

    const handleFinishedForm = ()=>{
      setFormData({});
      setCurrentStep(1);
    }

    return (
        <StepperContainer corner={channel?.elementCorners || "8px"} >
          <div style={{width:'100%',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <Logo className="channel" src={channel?.channelLogo ? `${process.env.REACT_APP_CDN}${channel.channelLogo}` : logo} 
                        alt="logo" 
                        onLoad={() => setIsImageLoaded(true)} />
            <LanguageTooltip page={"login channel"} />
          </div>
            <StepperWrapper color={channel?.primaryColor}>
                {STEPS.map((step, index) => (
                    <StepItem key={step.number} active={currentStep === step.number} completed={currentStep > step.number}>
                        <StepNumber color={channel?.primaryColor} active={currentStep === step.number} completed={currentStep > step.number}>
                            {step.number}
                        </StepNumber>
                        <StepLabel color={channel?.primaryColor} active={currentStep === step.number}>{t(step.label)}</StepLabel>
                    </StepItem>
                ))}
            </StepperWrapper>

            <FormStep
                key={formData}  
                title={stepContent.title}
                description={stepContent.description} 
                bottomTitle={stepContent?.bottomTitle}
                bottomDescription={stepContent?.bottomDescription}
                data={formData}
                onNext={handleNext}
                onBack={(currentStep > 1 && currentStep < 5) ? handleBack : undefined} 
                formFields={stepContent.fields}
                step={currentStep}
                setLoaderBox={setLoaderBox}
                setMessage={setMessage}
                api={api}
                handleFinishedForm={handleFinishedForm}
                channel={channel}
            />
        </StepperContainer>
    )
}

const FormStep = ({ data, onNext, onBack, formFields = [], title, description,bottomTitle,bottomDescription, step,setLoaderBox,setMessage, api, handleFinishedForm, channel }) => {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [copied, setCopied] = useState(false);
    const [formData , setFormData] = useState(data);

    const [mobileValidationError, setMobileValidationError] = useState();

    const handleSubmit = async (e) => {
      e.preventDefault();

      // Validation Check
      if ((step === 3 || step === 4) && mobileValidationError) {
        return;
      }

      console.log("data1", data);

      // extract basic form data
      const formData = new FormData(e.target);
      const values = {};
      formFields.forEach((field) => {
        if (formData.get(field.name)) values[field.name] = formData.get(field.name);
      });

      // ==============================================================
      // STEP 4: MAIN SUBMISSION (Generate Keys & Encrypt Everything)
      // ==============================================================
      if (step === 4) {
        try {
          // setLoaderBox(true);

          // 1. GENERATE IDENTIFIERS
          // We generate the Pass locally now, so the user holds the only key.
          const { caseId, secretKey, fullPass } = generateAccessPass();

          // [NEW] Generate the "Access Token" (Zero-Knowledge Proof)
          // We send this SHA-256 hash to the server. 
          // The server stores it. The server NEVER sees 'secretKey'.
          const encoder = new TextEncoder();
          const secretData = encoder.encode(secretKey);
          const hashBuffer = await window.crypto.subtle.digest('SHA-256', secretData);
          const accessHash = arrayBufferToBase64(hashBuffer);

          // 2. GENERATE MASTER AES KEY (The "Blue Key" for this case)
          const caseAesKey = await window.crypto.subtle.generateKey(
            { name: "AES-GCM", length: 256 },
            true,
            ["encrypt", "decrypt"]
          );

          // 3. CLEAN UP DATA BASED ON ANONYMITY SELECTION (BEFORE ENCRYPTION)
          // Get the current anonymity value
          const currentAnonymity = data.anonymity || values.anonymity;
          
          // Fields that should only be submitted when confidential
          const confidentialOnlyFields = ['whistleblowerName', 'whistleblowerEmail', 'whistleblowerMobileNumber'];
          
          // Create a cleaned version of data - remove fields not applicable to current anonymity
          const cleanedData = { ...data };
          const cleanedValues = { ...values };
          
          if (currentAnonymity === 'anonymous') {
            // Clear confidential-only fields when anonymous
            confidentialOnlyFields.forEach(fieldName => {
              delete cleanedData[fieldName];
              delete cleanedValues[fieldName];
            });
          }

          // Encrypt using cleaned data
          const encryptedTitle = (cleanedData.title || cleanedValues.title) ? await encryptData(cleanedData.title || cleanedValues.title, caseAesKey) : null;
          const encryptedDesc = (cleanedData.description || cleanedValues.description) ? await encryptData(cleanedData.description || cleanedValues.description, caseAesKey) : null;
          const encryptedName = (cleanedData.whistleblowerName || cleanedValues.whistleblowerName) ? await encryptData(cleanedData.whistleblowerName || cleanedValues.whistleblowerName, caseAesKey) : null;
          const encryptedMobile = (cleanedData.whistleblowerMobileNumber || cleanedValues.whistleblowerMobileNumber) ? await encryptData(cleanedData.whistleblowerMobileNumber || cleanedValues.whistleblowerMobileNumber, caseAesKey) : null;

          


          // 4. LOCK 1: FOR COMPANY (Encrypt AES Key with Company Public Key)
          if (!channel?.company?.public_key) {
            throw new Error("Security Error: Company has no public key.");
          }
          
          const companyPubKey = await importPublicKey(channel.company.public_key);
          const rawAesKey = await window.crypto.subtle.exportKey("raw", caseAesKey);
          
          const encryptedForCompany = await window.crypto.subtle.encrypt(
            { name: "RSA-OAEP" },
            companyPubKey,
            rawAesKey
          );

          // 5. LOCK 2: FOR SUBMITTER (Encrypt AES Key with Pass Secret)
          // Derive a Key from the "secretKey" string
          const submitterSalt = window.crypto.getRandomValues(new Uint8Array(16));
          const keyMaterial = await window.crypto.subtle.importKey(
            "raw", 
            new TextEncoder().encode(secretKey), 
            "PBKDF2", 
            false, 
            ["deriveKey"]
          );
          const derivedPassKey = await window.crypto.subtle.deriveKey(
            { name: "PBKDF2", salt: submitterSalt, iterations: 100000, hash: "SHA-256" },
            keyMaterial,
            { name: "AES-GCM", length: 256 },
            false,
            ["encrypt"]
          );

          const submitterIv = window.crypto.getRandomValues(new Uint8Array(12));
          const encryptedForSubmitter = await window.crypto.subtle.encrypt(
            { name: "AES-GCM", iv: submitterIv },
            derivedPassKey,
            rawAesKey
          );

          // 6. PREPARE CUSTOM FIELDS
          // Process custom fields - encrypt those marked for encryption
          // Only include fields that should be visible based on anonymity
          const customFieldsPayload = [];
          const customFields = formFields.filter(field => field.isCustom);
          
          for (const field of customFields) {
            // Check visibility condition - skip fields not visible for current anonymity
            if (field.visibleWhen?.enabled) {
              const visibilityCondition = field.visibleWhen.anonymityValue;
              if (visibilityCondition !== 'both' && visibilityCondition !== currentAnonymity) {
                continue; // Don't include this field in payload
              }
            }
            
            const fieldValue = cleanedData[field.name] || cleanedValues[field.name];
            if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
              if (field.isEncrypted) {
                // Encrypt the custom field value
                const encryptedValue = await encryptData(String(fieldValue), caseAesKey);
                customFieldsPayload.push({
                  fieldId: field.fieldId,
                  fieldKey: field.fieldKey || field.name.replace('custom_', ''),
                  isEncrypted: true,
                  encryptedValue: encryptedValue
                });
              } else {
                // Store plain value
                customFieldsPayload.push({
                  fieldId: field.fieldId,
                  fieldKey: field.fieldKey || field.name.replace('custom_', ''),
                  isEncrypted: false,
                  value: fieldValue
                });
              }
            }
          }

          // 7. PREPARE PAYLOAD
          // We send encrypted blobs, not plain text.
          // Filter out custom_* fields from cleanedData since they're in customFields array
          const filteredData = { ...cleanedData };
          Object.keys(filteredData).forEach(key => {
            if (key.startsWith('custom_')) {
              delete filteredData[key];
            }
          });

          // Build payload - only include confidential fields when anonymity is confidential
          const payload = {
            ...filteredData,
            channel: channel._id,
            company: channel?.company?._id,
            caseId: caseId,
            whistleblower_hash: accessHash,
            
            title: encryptedTitle,
            description: encryptedDesc,

            // Custom fields array
            customFields: customFieldsPayload,

            // The Keys to unlock it
            keys: {
              // Admin Lock
              company_lock: arrayBufferToBase64(encryptedForCompany),
              // Submitter Lock (Salt/IV needed to derive key from pass)
              whistlerblower_lock: {
                ciphertext: arrayBufferToBase64(encryptedForSubmitter),
                iv: arrayBufferToBase64(submitterIv),
                salt: arrayBufferToBase64(submitterSalt)
              }
            }
          };

          // Only add whistleblower contact fields if confidential
          if (currentAnonymity === 'confidential') {
            payload.whistleblowerName = encryptedName;
            payload.whistleblowerMobileNumber = encryptedMobile;
          }

          console.log("payload for api", payload);

          // 7. SEND TO API
          await postData(payload, api) // Ensure 'api' points to your create endpoint
            .then((response) => {
              if (response.status === 200 || response.status === 201) {
                setMessage({ type: 1, content: t("dataSubmit"), proceed: t("okay"), code: 200 });
                
                // CRITICAL: Pass the generated 'fullPass' to the next step
                // The backend doesn't return the pass anymore (it can't!), we generated it.
                values.pass = fullPass; 
                
                // Optional: Pass the rawAESKey in memory to Step 5 to save re-decryption time
                // values.tempKey = caseAesKey; 
                
                onNext(values);
              } else {
                console.log(response);
                setMessage({ type: 1, content: response.data?.message || "Error", proceed: t("okay"), code: response.status });
              }
              setLoaderBox(false);
            })
            .catch((err) => {
              console.error(err);
              setMessage({ type: 1, content: "Server Error", proceed: "Try Again" });
              setLoaderBox(false);
            });

        } catch (error) {
          console.error('Encryption/Submission Error:', error);
          setLoaderBox(false);
          setMessage({ type: 1, content: "Security check failed: " + error.message, proceed: "Okay" });
        }
      } 

      // ==============================================================
      // STEP 5: ADD EMAIL (Re-Lock using the Pass)
      // ==============================================================
      else if (step === 5 && data.whistleblowerEmail) {
        try {
          setLoaderBox(true);

          // We need to encrypt the email with the SAME AES Key as Step 4.
          // 1. Parse the Pass
          if(!data.pass) throw new Error("Missing Pass from previous step");
          const [caseId, secretKey] = data.pass.split(":");

          // [NEW] Calculate the Access Hash again
          const encoder = new TextEncoder();
          const hashBuffer = await window.crypto.subtle.digest('SHA-256', encoder.encode(secretKey));
          const accessHash = arrayBufferToBase64(hashBuffer);

          // 2. We need the 'whistlerblower_lock' to get the AES Key.
          // You might need to fetch the case data first if you didn't keep 'tempKey' in memory.
          // Assuming we fetch it to be robust:
          const caseResponse = await getData({}, `${api}/caseId/${caseId}`); // Adjust endpoint to get case keys
          const submitterLock = caseResponse.data.keys.whistlerblower_lock;

          // 3. Unlock the Master AES Key using the Pass (Secret Key)
          const salt = base64ToUint8Array(submitterLock.salt);
          const iv = base64ToUint8Array(submitterLock.iv);
          const encryptedKey = base64ToUint8Array(submitterLock.ciphertext);

          const keyMaterial = await window.crypto.subtle.importKey(
            "raw", 
            new TextEncoder().encode(secretKey), 
            "PBKDF2", 
            false, 
            ["deriveKey"]
          );
          const derivedPassKey = await window.crypto.subtle.deriveKey(
            { name: "PBKDF2", salt: salt, iterations: 100000, hash: "SHA-256" },
            keyMaterial,
            { name: "AES-GCM", length: 256 },
            false,
            ["decrypt"]
          );

          const rawAesKeyBuffer = await window.crypto.subtle.decrypt(
            { name: "AES-GCM", iv: iv },
            derivedPassKey,
            encryptedKey
          );
          
          const caseAesKey = await window.crypto.subtle.importKey("raw", rawAesKeyBuffer, "AES-GCM", true, ["encrypt"]);

          // 4. Encrypt the Email
          // const emailIv = window.crypto.getRandomValues(new Uint8Array(12));
          // const emailEncoded = new TextEncoder().encode(data.whistleblowerEmail);
          // const emailCipher = await window.crypto.subtle.encrypt(
          //   { name: "AES-GCM", iv: emailIv },
          //   caseAesKey,
          //   emailEncoded
          // );

          const encryptedEmailPayload = await encryptData(data.whistleblowerEmail, caseAesKey);

          const headers = {
            'x-submitter-token': accessHash // PROOF OF IDENTITY
          };

          // 5. Send Update
          await putData({ whistleblowerEmail: encryptedEmailPayload }, `${api}/whistleblower/${caseId}`, null, null, headers)
            .then((response) => {
              if (response.status === 200) {
                setMessage({ type: 1, content: t("dataSubmit"), proceed: t("okay"), code: 200 });
                onNext(response.data);
                handleFinishedForm();
                setFormData(null);
              } else {
                setMessage({ type: 1, content: "Something went wrong", proceed: "Try Again" });
              }
              setLoaderBox(false);
            })
            .catch(() => {
              setMessage({ type: 1, content: "Server Error", proceed: "Try Again" });
              setLoaderBox(false);
            });

        } catch (error) {
          console.error('Email Encryption Error:', error);
          setLoaderBox(false);
        }
      } 
      
      // ==============================================================
      // FINAL STEP (Finish)
      // ==============================================================
      else if (step === 5) {
        handleFinishedForm();
        setFormData(null);
      } else {
        onNext(values);
      }
    };

    const handleCopy = (value) => {
      navigator.clipboard.writeText(value).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    };

    const handleSelectChange = (index, event, id, type = "text", sub = null, data = {}) => {
        onNext({[id]: event.id ?? event._id}, false)
    }

    const handleDateChange = (index, event, id, type = "text", sub = null, data = {}) => {
      let value;
      if (type === "date") {
        if (event) {
          event.setHours(14, 0, 0, 0); // Set time to 14:00 (2:00 PM)
          // if(field.name==="startDate"){
          //   if(formValues.endDate && new Date(event) > new Date(formValues.endDate)){
          //     updateValue["endDate"] = event.toISOString();
          //   }
          //   if(formValues.endDate) updateState[id].maxDate = new Date(updateValue.endDate)
          //     updateState.forEach((value) => {
          //       if(value.name === "endDate") value.minDate = new Date(event);
          //     }); 
          // } else if(field.name==="endDate"){
          //   if(formValues.startDate && new Date(event) < new Date(formValues.startDate)){
          //     updateValue["startDate"] = event.toISOString();
          //   }
          //   if(formValues.startDate) updateState[id].minDate = new Date(updateValue.startDate);
          //   updateState.forEach((value) => {
          //     if(value.name === "startDate") value.maxDate = new Date(event);
          //   });
          // } else if(field.name==="contractFrom"){
          //   if(formValues.contractEnd && new Date(event) > new Date(formValues.contractEnd)){
          //     updateValue["contractEnd"] = event.toISOString();
          //   }
          //   if(formValues.contractEnd) updateState[id].maxDate = new Date(updateValue.contractEnd)
          //   updateState.forEach((value) => {
          //     if(value.name === "contractEnd") value.minDate = new Date(event);
          //   }); 
          // } else if(field.name==="contractEnd"){
          //   if(formValues.contractFrom && new Date(event) < new Date(formValues.contractFrom)){
          //     updateValue["contractFrom"] = event.toISOString();
          //   }
          //   if(formValues.contractFrom) updateState[id].minDate = new Date(updateValue.contractFrom);
          //   updateState.forEach((value) => {
          //     if(value.name === "contractFrom") value.maxDate = new Date(event);
          //   });
          // }
          value = event.toISOString();
        } else {
          value = "";
        }
        onNext({[id]: value}, false)
      }
    }

    const handleMultiSelectChange = (idx, event, id, type = "text", sub = null, data = {}) => {
      const items = (formData[id] && Array.isArray(formData[id])) ? [...formData[id]] : [];
      const index = items.findIndex((item) => item === event.id);

      if (index === -1) {
        // If event._id doesn't exist, push it to the items array
        items.push(event.id);
      } else {
        // If event._id already exists, remove it from the items array
        items.splice(index, 1);
      }

      setFormData((prev) => ({...prev, [id]: items}))

      onNext({[id]: items}, false)
    }

    const handleFileChange = (name, event) => {
      onNext({[name]: event.target.files}, false)
    }

    return (
      <FormContainer>
        <FormTitle>{t(title)}</FormTitle>
        <FormDescription>{t(description)}</FormDescription>
        {step > 0 && step < 5 && channel.anonymity !== "confidential" && (data?.anonymity ? data?.anonymity === "anonymous" : true) && <AlertBox>{t("anonymousMessage")}</AlertBox>}
        <form onSubmit={handleSubmit}>
          {formFields.map((field) => {
            // Handle visibility conditions for custom fields
            if (field.isCustom && field.visibleWhen?.enabled) {
              const anonymityValue = data?.anonymity;
              const visibilityCondition = field.visibleWhen.anonymityValue;
              
              // If "both" or not set, always show
              if (visibilityCondition !== "both") {
                // Hide if anonymity value doesn't match the visibility condition
                if (anonymityValue && anonymityValue !== visibilityCondition) {
                  return null;
                }
              }
            }

            switch (field.type) {
              // Render a regular text input
              case "text":
              case "password":
              case "email":
              // case "number":
              // case "date":
              case "color": return (<>
              <FormGroup key={field.name} style={{ position: 'relative' }}>
              <Label htmlFor={field.name}>{t(field.label)}</Label>
              <Input
                type={field.type}
                id={field.name}
                name={field.name}
                placeholder={t(field?.placeholder)}
                defaultValue={data[field.name] || field.default}
                minLength={field?.minimum}
                maxLength={field?.maximum}
                required={field?.required}
                disabled={field?.disabled}
                className={`${field.name}`}
              />
              {field.copy && (
                <Button
                  variant="primary"
                  style={{ position: 'absolute', right: window.innerWidth > 768 ? 0 : -20, top: '69.6%', transform: 'translateY(-50%)' }}
                  type="button"
                  onClick={() => handleCopy(data[field.name] || field.default)}
                >
                  {copied ? t("copied") : t(field.copy)}
                </Button>
              )}
            </FormGroup>
              {field.name==="pass" && (channel.emailForNotification==="always" || (channel.emailForNotification==="confidential" && data.anonymity==="confidential") || (channel.emailForNotification==="anonymous" && data.anonymity==="anonymous")) && <FormGroup key={field.name} style={{ position: 'relative' }}>
                <Label htmlFor={"whistleblowerEmail"}>{t("email")}</Label>
                <Input
                  type={"email"}
                  id={"whistleblowerEmail"}
                  name={"whistleblowerEmail"}
                  placeholder={t("enterEmail")}
                  required={false}
                  value={data.whistleblowerEmail}
                  onChange={(e) => onNext({whistleblowerEmail: e.target.value}, false)}
                />
                <p>{t("This email will not be shown to anybody. The only use of the email is to notify you when relevant changes to your case are made, such as:")}</p>

            </FormGroup>}
            </>);

            case "number":
              const value = isNaN(data[field.name] || field.default) ? null : data[field.name] || field.default;
              const handleKeyDown = (event) => {
                if (event.keyCode === 38 || event.keyCode === 40) {
                  // Prevent the default behavior for up and down arrow keys
                  event.preventDefault();
                }
              };
              return (
                <FormGroup key={field.name} style={{ position: 'relative' }}>
                  <Label htmlFor={field.name}>{t(field.label)}</Label>
                  <Input
                    type={field.type}
                    id={field.name}
                    name={field.name}
                    defaultValue={data[field.name] || field.default}
                    minLength={field?.minimum}
                    maxLength={field?.maximum}
                    required={field?.required}
                    disabled={field?.disabled}
                    className={`${field.name}`}
                    onKeyDown={handleKeyDown} // Attach the onKeyDown event handler
                    onWheel={(e) => e.target.blur()}
                    autoComplete="on"
                  />
                </FormGroup>
              );

            case "textarea": return (
              <FormGroup key={field.name} style={{ position: 'relative' }}>
              <Label htmlFor={field.name}>{t(field.label)}</Label>
              <Textarea
                type={field.type}
                id={field.name}
                name={field.name}
                placeholder={t(field?.placeholder)}
                defaultValue={data[field.name] || field.default}
                minLength={field?.minimum}
                maxLength={field?.maximum}
                required={field?.required}
                disabled={field?.disabled}
                className={`${field.name}`}
              />
            </FormGroup>
            );

            case "select": return (
              <FormGroup onClick={(e) => e.preventDefault()} key={field.name} style={{ position: 'relative' }}>
              {/* <Label htmlFor={field.name}>{t(field.label)}</Label> */}
              <CustomSelect {...field} name={field.name} id={field.name} value={data[field.name] || field.default} onSelect={handleSelectChange}></CustomSelect>
            </FormGroup>
            )

            case "multiSelect": return (
              <FormGroup onClick={(e) => e.preventDefault()} key={field.name} style={{ position: 'relative' }}>
              {/* <Label htmlFor={field.name}>{t(field.label)}</Label> */}
              <MultiSelect {...field} name={field.name} id={field.name} value={(formData && Array.isArray(formData[field.name]) && formData[field.name]) || field.default} onSelect={handleMultiSelectChange}></MultiSelect>
            </FormGroup>
            )

            case "date": 
              return (
                <FormGroup onClick={(e) => e.preventDefault()} key={field.name} style={{ position: 'relative' }}>
                <Label htmlFor={field.name}>{t(field.label)}</Label>
                <DateTimePicker 
                  disabled={field.disabled || false} 
                  placeholderText={t("DD.MM.YYYY")} 
                  minDate={field.minDate ? new Date(field.minDate) : null} 
                  maxDate={field.maxDate ? new Date(field.maxDate) : null} 
                  className={`input`} 
                  dateFormat={"dd.MM.yyyy"} 
                  type={field.type} 
                  selected={data[field.name] ? new Date(data[field.name]) : null} 
                  required={field.required}
                  onChange={(event) => handleDateChange(0, event, field.name, field.type)} 
                />
              </FormGroup>
              )

            case "radio":
              return (
                <>
                <FormGroup key={field.name} style={{ position: "relative" }}>
                  {field?.options?.map((opt, idx) => (
                    <div style={{ marginBottom: "0.5rem" }} key={`opt-${idx}`}>
                      <input
                        type="radio"
                        id={`${field.name}-${opt.name}`}
                        name={field.name}
                        value={opt.name}
                        disabled={field?.disabled}
                        checked={data[field.name] === opt.name} // ✅ controlled correctly
                        onChange={(e) => onNext({ [field.name]: e.target.value }, false)}
                        style={{cursor: 'pointer'}}
                      />
                      <label
                        htmlFor={`${field.name}-${opt.name}`}
                        style={{ marginLeft: "8px", fontSize: "16px", fontWeight: 700, cursor: 'pointer' }}
                      >
                        {t(opt.title)}
                      </label>
                      <p style={{ marginLeft: "30px", marginTop: "5px" }}>{t(opt.desc)}</p>
                    </div>
                  ))}
                </FormGroup>
                {(data[field.name]==="confidential") && <>
                 <FormGroup key={"whistleblowerName"} style={{ position: "relative" }}>
                  <div style={{ marginBottom: "0.5rem" }} >
                    <Label htmlFor={"whistleblowerName"}>{t("name")}</Label>
                    <Input
                      type={"text"}
                      id={"whistleblowerName"}
                      name={"whistleblowerName"}
                      placeholder={t("enterName")}
                      minLength={1}
                      maxLength={100}
                      required={true}
                      disabled={field?.disabled}
                      value={data.whistleblowerName}
                      onChange={(e)=> onNext({whistleblowerName: e.target.value}, false)}
                    />
                  </div>
                </FormGroup>
                <FormGroup key={"whistleblowerMobileNumber"} style={{ position: "relative" }}>
                  <div style={{ marginBottom: "0.5rem" }} >
                    <Label htmlFor={"whistleblowerMobileNumber"}>{t("mobileNumber")}</Label>
                    <Input
                      type={"text"}
                      id={"whistleblowerMobileNumber"}
                      name={"whistleblowerMobileNumber"}
                      placeholder={t("enterNumber")}
                      minLength={1}
                      maxLength={100}
                      required={false}
                      disabled={field?.disabled}
                      value={data.whistleblowerMobileNumber}
                      onChange={(e)=> {
                        onNext({whistleblowerMobileNumber: e.target.value}, false)
                        let v = e.target.value;
                        if(v?.startsWith("+")) v = v.substring(1);
                        if(v.length > 0 ? /^[0-9]\d{1,13}$/.test(v) : true){
                          setMobileValidationError(null);
                        }
                        else
                          setMobileValidationError(t("mobileValidationError", {mobile: e.target.value}))
                      }}
                    />
                    {mobileValidationError && <p style={{ color: 'red' }}>{mobileValidationError}</p>}
                  </div>
                </FormGroup>
                </>
                }
                </>
              );


            case "image":
            case "file":
              function formatSize(sizeInBytes) {
                if (sizeInBytes < 1024) {
                  return sizeInBytes + " bytes";
                } else if (sizeInBytes < 1024 * 1024) {
                  return (sizeInBytes / 1024).toFixed(2) + " KB";
                } else {
                  return (sizeInBytes / (1024 * 1024)).toFixed(2) + " MB";
                }
              }
              const size = formatSize((data[field.name]?.length > 0 && data[field.name][0]) ? data[field.name][0]?.size : 0);
              const handleButtonClick = () => {
                fileInputRef.current.click();
              };
              return (
                <FileContainer style={{marginBottom: "1.5rem"}}>
                  <button disabled={field?.disabled} type="button" onClick={handleButtonClick}>
                    <UploadIcon />
                    {t("upload", { label: t(field.label) }) + (field.required ? " *" : "") + (data[field.name]?.length > 0 ? ` : ${data[field.name][0]?.name} (${size})` : "")}
                  </button>
                  <Input disabled={field.disabled || false} ref={fileInputRef} style={{ display: "none" }} accept={field.type === "image" ? `image/*` : ``} className={`input ${data[field.name]?.length > 0 ? "shrink" : ""}`} placeholder={field.formType === "put" ? "" : t(field.placeholder)} type={`file`} onChange={(event) => handleFileChange("file", event)} />
                  {/* {props.error?.length > 0 && <ErrorMessage dangerouslySetInnerHTML={{ __html: props.error }} />} */}
                </FileContainer>
              );

            }})}
          {bottomTitle && <FormTitle>{t(bottomTitle)}</FormTitle>}
          {bottomDescription && <FormDescription>{t(bottomDescription)}</FormDescription>}
            <ButtonGroup>
              {onBack && (
                <Button color={channel?.primaryColor} corner={channel?.buttonCorners} type="button" onClick={onBack}>
                  {t("back")}
                </Button>
              )}
              <Button color={channel?.primaryColor} corner={channel?.buttonCorners} type="submit" variant="primary">
                {step === 4 ? t("submit") : step===5 ? t("finish") : t("next")}
              </Button>
            </ButtonGroup>
        </form>
      </FormContainer>
    )
}